import React, { useState, useEffect } from 'react';
import { Client, User, clientsAPI, usersAPI } from '../../services/api';
import {
  UserPlusIcon,
  UserMinusIcon,
  CheckIcon,
  XMarkIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader, Button, Badge, Spinner, Modal, EmptyState, SearchInput, PageHero } from '../ui';

const ClientAssignment: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsData, usersData] = await Promise.all([
        clientsAPI.getAll({ limit: 1000 }), // Get all clients without pagination
        usersAPI.getAll(),
      ]);
      // clientsAPI.getAll returns paginated response with { data, pagination }
      setClients(Array.isArray(clientsData) ? clientsData : clientsData.data || []);
      setUsers(usersData.filter(user => user.role === 'USER')); // Only salesmen
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignClient = async (clientId: number, userId: number) => {
    try {
      setAssignmentLoading(true);
      const updatedClient = await clientsAPI.assignClient(clientId, userId);

      // Update the clients list
      setClients(clients.map(client =>
        client.id === clientId ? updatedClient : client
      ));

      setSelectedClient(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign client');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleUnassignClient = async (clientId: number) => {
    try {
      setAssignmentLoading(true);
      const updatedClient = await clientsAPI.unassignClient(clientId);

      // Update the clients list
      setClients(clients.map(client =>
        client.id === clientId ? updatedClient : client
      ));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unassign client');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const filteredClients = Array.isArray(clients) ? clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.assignedUser?.firstName &&
      `${client.assignedUser.firstName} ${client.assignedUser.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.assignedUser?.email && client.assignedUser.email.toLowerCase().includes(searchTerm.toLowerCase()))
  ) : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Enhanced Hero Section */}
      <PageHero
        title="Client Assignment"
        subtitle="Assign clients to salesmen or manage existing assignments"
        icon={<UsersIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white drop-shadow-lg" />}
        accentColor="blue"
      >
        <div className="flex items-center space-x-2 mt-2">
          <Badge variant="primary">{filteredClients.length} clients</Badge>
          <Badge variant="secondary">{users.length} salesmen</Badge>
        </div>
      </PageHero>

      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50">
          <CardBody className="p-4">
            <div className="text-sm text-error-600">{error}</div>
          </CardBody>
        </Card>
      )}

      {/* Search */}
      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search clients by name, type, or assigned salesman..."
      />

      {/* Clients Table */}
      <Card variant="glass" hoverable clickable>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Currently Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Deals
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredClients.map((client) => (
                  <tr key={client.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-sm">
                            {client.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-neutral-900">
                            {client.name}
                          </div>
                          <div className="text-sm text-neutral-500">
                            <Badge variant="neutral" size="sm">{client.type}</Badge>
                            <span className="ml-2">{client.email || 'No email'}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-neutral-900">
                        {client.createdBy
                          ? `${client.createdBy.firstName || ''} ${client.createdBy.lastName || ''}`.trim() || client.createdBy.email
                          : 'Unknown'
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.assignedUser ? (
                        <Badge variant="success">
                          {`${client.assignedUser.firstName || ''} ${client.assignedUser.lastName || ''}`.trim() || client.assignedUser.email}
                        </Badge>
                      ) : (
                        <Badge variant="warning">Unassigned</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="neutral">{client._count?.deals || 0}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {client.assignedUserId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnassignClient(client.id)}
                            disabled={assignmentLoading}
                            className="text-error-600 hover:text-error-700 hover:bg-error-50"
                            title="Unassign"
                          >
                            <UserMinusIcon className="h-5 w-5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedClient(client)}
                          disabled={assignmentLoading}
                          title="Assign to salesman"
                        >
                          <UserPlusIcon className="h-5 w-5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredClients.length === 0 && (
              <EmptyState
                title="No clients found"
                description="Try adjusting your search criteria."
                icon={<UsersIcon className="h-12 w-12" />}
              />
            )}
          </div>
        </CardBody>
      </Card>

      {/* Assignment Modal */}
      <Modal
        isOpen={!!selectedClient}
        onClose={() => setSelectedClient(null)}
        title={`Assign Client: ${selectedClient?.name || ''}`}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Select a salesman to assign this client to:
          </p>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {users.map((user) => (
              <button
                key={user.id}
                onClick={() => selectedClient && handleAssignClient(selectedClient.id, user.id)}
                disabled={assignmentLoading}
                className="w-full text-left p-4 border border-neutral-200 rounded-lg hover:bg-neutral-50 hover:border-primary-300 disabled:opacity-50 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                    <span className="text-primary-600 font-medium text-sm">
                      {(user.firstName?.charAt(0) || user.email.charAt(0)).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-neutral-900">
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.email
                      }
                    </div>
                    <div className="text-sm text-neutral-500">{user.email}</div>
                  </div>
                </div>
                {selectedClient?.assignedUserId === user.id && (
                  <Badge variant="success">
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Current
                  </Badge>
                )}
              </button>
            ))}
          </div>

          <div className="flex justify-end pt-4">
            <Button variant="ghost" onClick={() => setSelectedClient(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClientAssignment;
